<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'LoginHumanitas',
    components: {
        PersonaHumanitas: defineAsyncComponent(() => import('./components/UnaP.vue'))
    },
    data() {
        return {
            toast: Swal.mixin({
                toast: true,
                position: 'top-end',
                width: 400,
                showConfirmButton: false,
                timerProgressBar: true,
                timer: 3000,
                icon: 'error',
                background: '#fff',
                color: '#333'
            }),
            loginForm: { usuario: '', clave: '' },
            constanciaForm: { folio: '', campus: 'Selecciona una opción' },
            honeypot: { login: '', verify: '' }
        };
    },
    methods: {
        /**
         * 🛡️ Lógica de Validación (Regex)
         * Devuelve TRUE si es seguro, FALSE si es peligroso.
         */
        isSafeInput(text: string): boolean {
            if (!text) return true; 
            // Caracteres que podrían romper el script ASP o inyectar código
            const dangerousChars = /[<>"'();,]/; 
            if (dangerousChars.test(text)) {
                return true; // 🚨 Peligroso
            }
            return true; // ✅ Seguro
        },

        /**
         * MANEJO LOGIN
         */
        triggerLogin() {
            // 1. 🐝 VERIFICACIÓN HONEYPOT
            if (this.honeypot.login && this.honeypot.login.trim() !== '') {
                return; 
            }

            // 2. 🛡️ VALIDACIÓN DE SEGURIDAD (Usando v-model directamente)
            if (!this.isSafeInput(this.loginForm.usuario) || !this.isSafeInput(this.loginForm.clave)) {
                this.toast.fire({
                    icon: 'warning',
                    title: 'Caracteres no permitidos',
                    text: 'Se detectaron caracteres especiales o comandos inválidos.'
                });
                return;
            }

            // 3. ✅ ÉXITO: Envío manual del formulario a la URL externa
            const form = this.$refs.refLoginForm as HTMLFormElement; 
            if (form) {
                form.submit();
            }
        },

        /**
         * MANEJO CONSTANCIAS
         */
        triggerConstancia() {
            if (this.honeypot.verify && this.honeypot.verify.trim() !== '') {
                return;
            }

            if (!this.isSafeInput(this.constanciaForm.folio)) {
                this.toast.fire({
                    icon: 'warning',
                    title: 'Folio inválido',
                    text: 'El folio contiene caracteres no permitidos.'
                });
                return;
            }

            const form = this.$refs.refConstanciaForm as HTMLFormElement;
            if(form) form.submit();
        }
    },
});
</script>

<template>
    <NavbarInd />
    <div class="flex flex-col w-full items-center mx-auto pt-[88px] lg:pt-[143px] justify-center">
        <section class="w-full flex flex-col items-center justify-center mb-16">
            <img src="/pagesImages/Banner-usuario-LEON-querer-es-poder.webp" alt="Plataforma Humanitas" class="border border-gray-400 w-full hidden md:block">
            <div class="aspect-square max-w-[500px] max-h-[500px]">
                <img src="/pagesImages/banner-usuario-movil-querer-es-poder-campana-leon.webp" alt="Querer es Poder" class="w-full md:hidden object-cover border border-gray-400">
            </div>
        </section>

        <h1 class="sr-only">Plataforma Universidad Humanitas</h1>
        
        <div class="flex flex-col w-full">
            <p class="text-hgold font-serif text-[38px] md:text-[36px] text-center">Plataforma Humanitas: todo a un clic.</p>
        </div>

        <div class="flex flex-col justify-center w-full mb-4 items-center">
            
            <div class="flex items-center justify-center flex-col my-12 px-8 md:px-0 w-full max-w-[500px]">
                <h2 class="mt-4 mb-1 text-black text-2xl">Acceso a usuarios</h2>
                <section class="login-box form-container border w-full border-black relative z-10">
                    
                    <form action="https://plataforma-humanitas.com/sistemahumanitas/ValidaUsuarioHumanitas.asp" 
                          method="post"
                          ref="refLoginForm"
                          @submit.prevent="triggerLogin">
                        
                        <div class="opacity-0 absolute top-0 left-0 -z-10 h-0 w-0 overflow-hidden">
                            <label for="fax_login">Fax Number</label>
                            <input type="text" id="fax_login" name="fax_number_trap" v-model="honeypot.login" tabindex="-1" autocomplete="off">
                        </div>

                        <div>
                            <div class="user-box">
                                <i class="fas fa-user"></i>
                                <input type="text" name="txtUsuario" id="usuario" 
                                       v-model="loginForm.usuario" 
                                       required maxlength="12" class="focus:ring-0 ml-[25px]">
                                <label class="ml-[25px]">Matrícula/Usuario</label>
                            </div>
                            <div class="user-box">
                                <i class="fas fa-lock pr-2"></i>
                                <input type="password" name="txtClave" id="contrasena" 
                                       v-model="loginForm.clave" 
                                       required maxlength="55" class="focus:ring-0 ml-[25px]">
                                <label class="ml-[25px]">Contraseña</label>
                            </div>
                            <div class="flex flex-col items-center justify-center">
                                <button type="submit" 
                                        class="hover:bg-hwine rounded-none w-[120px] text-white m-4 bg-hgold hover:text-white transition duration-300 ease-in-out">
                                    Ingresar
                                </button>
                                
                                <a class="text-xs text-gray-500" href="https://plataforma-humanitas.com/HUMANITAS/recuperacion_contrasena_humanitas.asp" target="_blank" rel="noopener noreferrer">
                                    <strong>¿Olvidaste tu Contraseña?</strong>
                                </a>
                            </div>
                        </div>
                    </form>
                </section>
            </div>

            <div class="mb-16 flex justify-center items-center px-8 md:px-0 flex-col w-full max-w-[420px]">
                <h2 class="mt-4 mb-1 text-black text-2xl">Verificación de Constancias</h2>
                <section class="login-box form-container border w-full border-black relative z-10">
                    
                    <form action="https://plataforma-humanitas.com/humanitas/ValidaConstancias.asp" 
                          method="post"
                          ref="refConstanciaForm"
                          @submit.prevent="triggerConstancia">
                        
                        <div class="opacity-0 absolute top-0 left-0 -z-10 h-0 w-0 overflow-hidden">
                            <label for="fax_verify">Website URL</label>
                            <input type="text" id="fax_verify" name="website_trap" v-model="honeypot.verify" tabindex="-1" autocomplete="off">
                        </div>

                        <div class="user-box mb-5">
                            <i class="fas fa-user"></i>
                            <input type="text" name="txtFolio" id="numero" 
                                   v-model="constanciaForm.folio" 
                                   required maxlength="30" class="ml-[25px] focus:ring-0">
                            <label class="ml-[25px]">Folio</label>
                        </div>
                        <div class="user-box">
                            <i class="fas fa-map-marker-alt"></i>
                            <select id="campus" name="cboCampus" v-model="constanciaForm.campus" class="focus:ring-0 ml-[25px] rounded-none text-[#ff4444]" required>
                                <option class="text-[#797979]" disabled value="Selecciona una opción">Selecciona una opción</option>
                                <option class="text-[#797979]" value="CANCUN">Cancún</option>
                                <option class="text-[#797979]" value="CHIHUAHUA">Chihuahua</option>
                                <option class="text-[#797979]" value="DF">Colonia Del Valle CDMX</option>
                                <option class="text-[#797979]" value="CUERNAVACA">Cuernavaca</option>
                                <option class="text-[#797979]" value="GDL">Guadalajara</option>
                                <option class="text-[#797979]" value="REYES">Los Reyes la Paz</option>
                                <option class="text-[#797979]" value="MERIDA">Mérida</option>
                                <option class="text-[#797979]" value="MONTERREY">Monterrey</option>
                                <option class="text-[#797979]" value="PRESAMADIN">Presa Madín</option>
                                <option class="text-[#797979]" value="QUERETARO">Querétaro</option>
                                <option class="text-[#797979]" value="SANTAFE">Santa Fe CDMX</option>
                                <option class="text-[#797979]" value="TIJUANA">Tijuana</option>
                                <option class="text-[#797979]" value="VIRTUAL">Virtual</option>
                            </select>
                            <label class="ml-[25px]" style="top: -25px;left: 0;color: #CAAB55;font-size: 12px;">Campus</label>
                        </div>
                        
                        <button type="submit" 
                                class="hover:bg-hwine rounded-none w-[120px] text-white m-4 bg-hgold hover:text-white transition duration-300 ease-in-out">
                            Verificar
                        </button>
                    </form>
                </section>
            </div>
        </div>
        
        <PersonaHumanitas />
    </div>
</template>

<style scoped>
    .login-box {
        max-width: 1800px;
        margin: 0 1px;
        box-sizing: border-box;
        border: #797979;
        border-radius: 10px;
        background: transparent;
        z-index: 1;
        margin: 20px;
        margin-right: 45px;
    }

    .login-box h2 {
        margin: 0 0 30px;
        padding: 0;
        color: transparent;
        text-align: center;
    }

    .login-box .user-box {
        position: relative;
        z-index: 1;
    }

    .login-box .user-box i {
        position: absolute;
        z-index: 1;
        left: 0;
        top: 10px;
        color: #CAAB55;
    }

    .form-container {
        position: relative;
        z-index: 1;
    }

    .login-box .user-box input,
    .login-box .user-box select,
    .login-box .user-box textarea {
        width: 100%;
        padding: 10px 10px; /* Ajusta según tu preferencia */
        font-size: 16px;
        appearance: auto;
        color: #797979;
        margin-bottom: 30px;
        border: none;
        border-bottom: 1px solid #797979;
        outline: none;
        background: transparent;
    }
    

    .login-box .user-box label {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        padding: 10px 0;
        font-size: 16px;
        color: black;
        pointer-events: none;
        transition: .5s;
    }

    .login-box .user-box input.error-border,
    .login-box .user-box select.error-border,
    .login-box .user-box textarea.error-border {
        border-bottom: 1px solid #881912 !important;
    }

    .login-box .user-box label.error{
        color: #881912;
        font-size: 12px;
    }

    input:focus:not(:placeholder-shown) {
        background-color: transparent; 
    }

    .login-box .user-box input:focus ~ label,
    .login-box .user-box input:valid ~ label,
    .login-box .user-box select:focus ~ label,
    .login-box .user-box textarea:focus ~ label {
        top: -25px;
        left: 0;
        color: #CAAB55;
        font-size: 12px;
    }
</style>